/**
 * The subject's fields — the ONE form behind "Thêm chủ thể", "Sửa chủ thể" and the primary
 * subject of "Thêm khách hàng" (a customer is an account plus its primary subject): Cá nhân /
 * Tổ chức (an organisation: an enterprise or a household business), Họ and Tên, birth date,
 * gender, nationality, position, the identity document (type, number, issue date) or the tax
 * code with "Tra cứu", the address (country, postal code — optional, 76000 when left empty in
 * Vietnam —, house number and street, province and ward from the administrative list), contact,
 * and an organisation's legal representative. The draft, its checks and the body sent:
 * ../../lib/subjectDraft.ts (the same for every screen).
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { SubjectGender, SubjectIdentifierType, SubjectProfile } from '../../api/areas/accounts';
import { TaxCodeLookup } from '../../components/TaxCodeLookup';
import { fullName } from '../../lib/subjectNames';
import { DEFAULT_POSTAL_CODE, GENDERS, isHousehold, kindPatch, orgTaxCode, personal, type OrgKind, type RepDraft, type RepMode, type SubjectDraft, type SubjectKind } from '../../lib/subjectDraft';
import { useApi } from '../../lib/useApi';
import { cn } from '../../lib/utils';
import { PlacePicker } from '../accounts/CustomerProfileFields';

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 dark:bg-input/30';

function F({ label, error, hint, className, children }: { label: ReactNode; error?: string | null; hint?: ReactNode; className?: string; children: ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the control is the child
    <label className={cn('grid content-start gap-1.5 text-sm', className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error ? <span className="text-xs text-critical">{error}</span> : hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

export interface SubjectFieldsProps {
  d: SubjectDraft;
  set: (patch: Partial<SubjectDraft>) => void;
  /** The message of a field's problems (field names as the subject API names them). */
  err: (field: string) => string | null;
  /** Editing a stored subject (its hints; the kind cannot change). */
  profile?: SubjectProfile;
}

/** The organisation's legal representative: keep, pick an individual subject of the customer,
 * enter a new one, or (a household business) its owner. */
function RepresentativeFields({ d, set, err, profile }: SubjectFieldsProps) {
  const { t } = useTranslation('accounts');
  const current = profile?.relationships?.find((r) => r.type === 'LEGAL_REPRESENTATIVE');
  const people = useApi(
    (signal) =>
      d.customerId ? api.listSubjects({ customer_id: d.customerId, subject_type: 'INDIVIDUAL', limit: 100 }, { signal }) : Promise.resolve({ items: [], next_after: null }),
    [d.customerId],
  );
  const items = people.status === 'success' ? people.data.items : [];
  const r = d.rep;
  const setRep = (patch: Partial<RepDraft>) => set({ rep: { ...r, ...patch } });
  const household = isHousehold(d);
  const modes: RepMode[] = profile ? ['keep', 'existing', 'new'] : household ? ['owner', 'existing', 'new'] : ['existing', 'new'];
  const vn = d.country.toUpperCase() === 'VN';
  return (
    <fieldset className="grid gap-3 sm:grid-cols-2" data-subject-representative>
      <legend className="mb-2 text-sm font-medium">{t('subjectForm.representative')}</legend>
      <p className="text-xs text-muted-foreground sm:col-span-2">
        {current ? t('subjectForm.representativeCurrent', { name: current.related_name ?? current.related_subject_id }) : t('subjectForm.representativeNone')} · {t('subjectForm.representativeHint')}
      </p>
      <div className="flex flex-wrap gap-2 sm:col-span-2" role="radiogroup" aria-label={t('subjectForm.representative')}>
        {modes.map((m) => (
          <Button key={m} type="button" size="sm" variant={d.repMode === m ? 'default' : 'outline'} aria-pressed={d.repMode === m} onClick={() => set({ repMode: m })}>
            {t(`subjectForm.representativeModes.${m}`)}
          </Button>
        ))}
      </div>
      {d.repMode === 'owner' ? <p className="text-xs text-muted-foreground sm:col-span-2">{t('subjectForm.representativeOwnerHint')}</p> : null}
      {d.repMode === 'existing' ? (
        <F label={t('subjectForm.representativePick')} error={err('representative.subject_id')} className="sm:col-span-2">
          <select className={selectClass} value={d.repSubjectId} disabled={!d.customerId} onChange={(e) => set({ repSubjectId: e.target.value })}>
            <option value="">{d.customerId ? (people.status === 'loading' ? t('subjectForm.loading') : t('subjectForm.choose')) : t('subjectForm.representativeCustomerFirst')}</option>
            {items.map((x) => (
              <option key={x.id} value={x.id}>
                {x.legal_name ?? x.id}
              </option>
            ))}
          </select>
        </F>
      ) : null}
      {d.repMode === 'new' ? (
        <>
          <F label={t('subjectForm.lastName')} error={err('representative.new.last_name') ?? err('representative.new.legal_name')}>
            <Input value={r.lastName} onChange={(e) => setRep({ lastName: e.target.value })} autoComplete="off" data-field="rep.last_name" />
          </F>
          <F label={t('subjectForm.firstName')} error={err('representative.new.first_name')}>
            <Input value={r.firstName} onChange={(e) => setRep({ firstName: e.target.value })} autoComplete="off" data-field="rep.first_name" />
          </F>
          <F label={t('subjectForm.position')} error={err('representative.new.position')}>
            <Input value={r.position} onChange={(e) => setRep({ position: e.target.value })} />
          </F>
          <F label={t('subjectForm.birthDate')} error={err('representative.new.birth_date')}>
            <Input type="date" value={r.birthDate} onChange={(e) => setRep({ birthDate: e.target.value })} data-field="rep.birth_date" />
          </F>
          <F label={t('subjectForm.gender')} error={err('representative.new.gender')}>
            <select className={selectClass} value={r.gender} data-field="rep.gender" onChange={(e) => setRep({ gender: e.target.value as SubjectGender | '' })}>
              <option value="">{t('subjectForm.choose')}</option>
              {GENDERS.map((g) => <option key={g} value={g}>{t(`subjectForm.genders.${g}`)}</option>)}
            </select>
          </F>
          <F label={t('subjectForm.idTypes.NATIONAL_ID')} error={err('representative.new.identifier.value')} hint={t('subjectForm.cccdHint')}>
            <Input value={r.cccd} inputMode="numeric" autoComplete="off" data-field="rep.identifier" onChange={(e) => setRep({ cccd: e.target.value })} />
          </F>
          <F label={t('subjectForm.nationality')} error={err('representative.new.nationality')}>
            <Input value={r.nationality} maxLength={2} onChange={(e) => setRep({ nationality: e.target.value.toUpperCase() })} />
          </F>
          <F label={t('subjectForm.email')} error={err('representative.new.email')}>
            <Input type="email" value={r.email} onChange={(e) => setRep({ email: e.target.value })} data-field="rep.email" />
          </F>
          <F label={t('subjectForm.phone')} error={err('representative.new.phone')} hint={t('subjectForm.phoneHint')}>
            <Input type="tel" value={r.phone} onChange={(e) => setRep({ phone: e.target.value })} data-field="rep.phone" />
          </F>
          {vn ? (
            <>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <Checkbox checked={r.sameAddress} onCheckedChange={(v) => setRep({ sameAddress: v === true })} />
                {t('subjectForm.representativeSameAddress')}
              </label>
              {r.sameAddress ? null : (
                <>
                  <PlacePicker
                    provinceId={r.provinceId}
                    wardId={r.wardId}
                    onChange={(x) => setRep({ ...(x.provinceId !== undefined ? { provinceId: x.provinceId } : {}), ...(x.wardId !== undefined ? { wardId: x.wardId } : {}) })}
                    provinceError={err('representative.new.address.province_id')}
                    wardError={err('representative.new.address.ward_id')}
                  />
                  <F label={t('subjectForm.street')} error={err('representative.new.address.street')} className="sm:col-span-2">
                    <Input value={r.street} onChange={(e) => setRep({ street: e.target.value })} />
                  </F>
                  <F label={t('subjectForm.postalCodeVn', { code: DEFAULT_POSTAL_CODE })} error={err('representative.new.address.postal_code')} hint={t('subjectForm.postalCodeHint')}>
                    <Input value={r.postalCode} inputMode="numeric" maxLength={6} placeholder={DEFAULT_POSTAL_CODE} onChange={(e) => setRep({ postalCode: e.target.value })} />
                  </F>
                </>
              )}
            </>
          ) : null}
        </>
      ) : null}
    </fieldset>
  );
}

/** Cá nhân / Tổ chức, and for an organisation its type (enterprise or household business). */
export function SubjectKindPicker({ d, set }: Pick<SubjectFieldsProps, 'd' | 'set'>) {
  const { t } = useTranslation('accounts');
  return (
    <div className="grid gap-2" data-subject-kind>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('subjectForm.kind')}>
        {(['INDIVIDUAL', 'ORGANIZATION'] as SubjectKind[]).map((k) => (
          <Button key={k} type="button" size="sm" variant={d.kind === k ? 'default' : 'outline'} aria-pressed={d.kind === k} onClick={() => set(kindPatch(k, d.orgKind))}>
            {t(`subjectForm.kinds.${k}`)}
          </Button>
        ))}
      </div>
      {d.kind === 'ORGANIZATION' ? (
        <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label={t('subjectForm.orgKind')}>
          <span className="text-xs text-muted-foreground">{t('subjectForm.orgKind')}</span>
          {(['ENTERPRISE', 'HOUSEHOLD_BUSINESS'] as OrgKind[]).map((k) => (
            <Button key={k} type="button" size="sm" variant={d.orgKind === k ? 'secondary' : 'ghost'} aria-pressed={d.orgKind === k} onClick={() => set(kindPatch('ORGANIZATION', k))}>
              {t(`subjectForm.orgKinds.${k}`)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Every field of a subject (the kind picker included when creating). */
export function SubjectFields(p: SubjectFieldsProps) {
  const { d, set, err, profile } = p;
  const { t } = useTranslation('accounts');
  const editing = Boolean(profile);
  const individual = d.kind === 'INDIVIDUAL';
  const household = isHousehold(d);
  const person = personal(d.kind, d.orgKind);
  const vn = d.country.toUpperCase() === 'VN';
  const kept = profile?.identifiers.find((i) => !['HOUSEHOLD_BUSINESS_CODE', 'BUSINESS_HOUSEHOLD_TAX_CODE'].includes(i.type));
  const name = fullName(d.lastName, d.firstName);
  return (
    <div className="grid gap-4" data-subject-fields>
      {!editing ? <SubjectKindPicker d={d} set={set} /> : null}

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium">{individual ? t('subjectForm.person') : household ? t('subjectForm.orgKinds.HOUSEHOLD_BUSINESS') : t('subjectForm.organization')}</legend>
        {individual ? null : (
          <>
            <F label={t('subjectForm.orgName')} error={err('legal_name')} className="sm:col-span-2">
              <Input value={d.legalName} onChange={(e) => set({ legalName: e.target.value })} autoComplete="off" data-field="legal_name" />
            </F>
            <F label={t('subjectForm.tradeName')}>
              <Input value={d.tradeName} onChange={(e) => set({ tradeName: e.target.value })} />
            </F>
            <F label={t('subjectForm.establishDate')} error={err('establish_date')}>
              <Input type="date" value={d.establishDate} onChange={(e) => set({ establishDate: e.target.value })} />
            </F>
            <p className="text-xs font-medium text-muted-foreground sm:col-span-2">{household ? t('subjectForm.owner') : t('subjectForm.contactPerson')}</p>
          </>
        )}
        <F label={t('subjectForm.lastName')} error={err('last_name')}>
          <Input value={d.lastName} onChange={(e) => set({ lastName: e.target.value })} autoComplete="off" data-field="last_name" />
        </F>
        <F label={t('subjectForm.firstName')} error={err('first_name')}>
          <Input value={d.firstName} onChange={(e) => set({ firstName: e.target.value })} autoComplete="off" data-field="first_name" />
        </F>
        <p className="text-xs text-muted-foreground sm:col-span-2">
          {person ? (name ? t('subjectForm.fullNamePreview', { name }) : t('subjectForm.namesHint')) : t('subjectForm.contactPersonHint')}
          {individual && err('legal_name') ? <span className="block text-critical">{err('legal_name')}</span> : null}
          {household && err('owner_name') ? <span className="block text-critical">{err('owner_name')}</span> : null}
        </p>
        {person ? (
          <>
            <F label={t('subjectForm.birthDate')} error={err('birth_date')} hint={editing && profile?.has_birth_date ? t('subjectForm.keptOnFile') : undefined}>
              <Input type="date" value={d.birthDate} onChange={(e) => set({ birthDate: e.target.value })} data-field="birth_date" />
            </F>
            <F label={t('subjectForm.gender')} error={err('gender')}>
              <select className={selectClass} value={d.gender} data-field="gender" onChange={(e) => set({ gender: e.target.value as SubjectGender | '' })}>
                <option value="">{t('subjectForm.choose')}</option>
                {GENDERS.map((g) => <option key={g} value={g}>{t(`subjectForm.genders.${g}`)}</option>)}
              </select>
            </F>
          </>
        ) : null}
        <F label={t('subjectForm.nationality')} error={err('nationality')}>
          <Input value={d.nationality} maxLength={2} onChange={(e) => set({ nationality: e.target.value.toUpperCase() })} />
        </F>
        <F label={individual ? t('subjectForm.position') : t('subjectForm.contactPosition')}>
          <Input value={d.position} onChange={(e) => set({ position: e.target.value })} />
        </F>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-2 text-sm font-medium">{t('subjectForm.identity')}</legend>
        <F label={t('subjectForm.idType')}>
          <select className={selectClass} value={d.idType} onChange={(e) => set({ idType: e.target.value as SubjectIdentifierType })}>
            {(person ? (['NATIONAL_ID', 'PASSPORT'] as const) : (['TAX_CODE', 'ENTERPRISE_CODE'] as const)).map((x) => (
              <option key={x} value={x}>
                {t(`subjectForm.idTypes.${x}`)}
              </option>
            ))}
          </select>
        </F>
        <F
          label={t('subjectForm.idValue')}
          error={err('identifier.value') ?? err('identifier.type')}
          hint={kept ? t('subjectForm.idKept', { hint: kept.hint ?? '****' }) : person && d.idType === 'NATIONAL_ID' ? t('subjectForm.cccdTaxHint') : !person ? t('subjectForm.taxCodeHint') : undefined}
        >
          <Input
            value={d.idValue}
            inputMode={d.idType === 'PASSPORT' ? 'text' : 'numeric'}
            autoComplete="off"
            data-field="identifier"
            onChange={(e) => set({ idValue: e.target.value })}
            onBlur={() => (person ? undefined : set({ idValue: orgTaxCode(d) }))}
          />
        </F>
        <F label={t('subjectForm.idIssuedAt')}>
          <Input type="date" value={d.idIssuedAt} onChange={(e) => set({ idIssuedAt: e.target.value })} />
        </F>
        {household ? (
          <>
            <F label={t('subjectForm.householdCode')} error={err('household_code')} className="sm:col-span-2">
              <Input value={d.householdCode} autoComplete="off" onChange={(e) => set({ householdCode: e.target.value })} />
            </F>
            <F label={t('subjectForm.householdTaxCode')} error={err('household_tax_code')} hint={t('subjectForm.householdTaxCodeHint')}>
              <Input value={d.householdTaxCode} inputMode="numeric" autoComplete="off" onChange={(e) => set({ householdTaxCode: e.target.value })} />
            </F>
          </>
        ) : null}
        {!person && vn && d.idValue.trim() ? (
          <TaxCodeLookup
            code={orgTaxCode(d)}
            representative={d.repMode === 'new' ? { last: d.rep.lastName, first: d.rep.firstName } : undefined}
            onUse={(f) => set({ legalName: f.name ?? d.legalName, tradeName: d.tradeName || (f.shortName ?? ''), establishDate: d.establishDate || (f.foundingDate ?? '') })}
          />
        ) : null}
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium">{t('subjectForm.address')}</legend>
        <F label={t('subjectForm.country')} error={err('address.country_code')}>
          <Input value={d.country} maxLength={2} onChange={(e) => set({ country: e.target.value.toUpperCase() })} data-field="country" />
        </F>
        <F label={vn ? t('subjectForm.postalCodeVn', { code: DEFAULT_POSTAL_CODE }) : t('subjectForm.postalCode')} error={err('address.postal_code')} hint={vn ? t('subjectForm.postalCodeHint') : undefined}>
          <Input value={d.postalCode} inputMode={vn ? 'numeric' : 'text'} maxLength={vn ? 6 : 12} placeholder={vn ? DEFAULT_POSTAL_CODE : undefined} onChange={(e) => set({ postalCode: e.target.value })} />
        </F>
        <F label={t('subjectForm.street')} error={err('address.street')} hint={vn ? t('subjectForm.streetHint') : undefined} className="sm:col-span-2">
          <Input value={d.street} onChange={(e) => set({ street: e.target.value })} data-field="street" />
        </F>
        {vn ? (
          <PlacePicker
            provinceId={d.provinceId}
            wardId={d.wardId}
            onChange={(x) => set({ ...(x.provinceId !== undefined ? { provinceId: x.provinceId } : {}), ...(x.wardId !== undefined ? { wardId: x.wardId } : {}) })}
            provinceError={err('address.province_id')}
            wardError={err('address.ward_id')}
          />
        ) : (
          <>
            <F label={t('subjectForm.city')} error={err('address.city')}>
              <Input value={d.city} onChange={(e) => set({ city: e.target.value })} data-field="city" />
            </F>
            <F label={t('subjectForm.state')}>
              <Input value={d.state} onChange={(e) => set({ state: e.target.value })} />
            </F>
          </>
        )}
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-3">
        <legend className="mb-2 text-sm font-medium">{t('subjectForm.contact')}</legend>
        <F label={t('subjectForm.email')} error={err('email')}>
          <Input type="email" value={d.email} onChange={(e) => set({ email: e.target.value })} data-field="email" />
        </F>
        <F label={t('subjectForm.phone')} error={err('phone')} hint={t('subjectForm.phoneHint')}>
          <Input type="tel" value={d.phone} onChange={(e) => set({ phone: e.target.value })} data-field="phone" />
        </F>
        <F label={t('subjectForm.fax')} error={err('fax')}>
          <Input type="tel" value={d.fax} onChange={(e) => set({ fax: e.target.value })} />
        </F>
      </fieldset>

      {!individual ? <RepresentativeFields {...p} /> : null}
    </div>
  );
}
